import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { WhatsAppIcon, TelegramIcon, ErrorIcon } from '../../../components/ui/simple-icons';
import ToggleSwitch from './ToggleSwitch';
import WhitelistManager from './WhitelistManager';
import { WhatsAppConfig, TelegramConfig, ApiConfig, removeAgentSharing, updateAgent } from '../../../lib/database';
import { supabase } from '../../../lib/supabase';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';

interface Step4DeployAndShareProps {
  agentId: string | undefined;
  formData: {
    whatsappConfig: WhatsAppConfig;
    telegramConfig: TelegramConfig;
    apiConfig: ApiConfig;
    sharable?: boolean;
  };
  updateFormData: (key: string, value: any) => void;
}

// Validation functions
const validatePhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^\+[1-9]\d{1,14}$/;
  return phoneRegex.test(phone);
};

const validateTelegramUser = (user: string): boolean => {
  const usernameRegex = /^@[a-zA-Z0-9_]{5,32}$/;
  const userIdRegex = /^[0-9]{1,10}$/;
  return usernameRegex.test(user) || userIdRegex.test(user);
};

const SharedWithList: React.FC<{ agentId: string }> = ({ agentId }) => {
  const [sharedUsers, setSharedUsers] = useState<{ id: string; user_email: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userToRemove, setUserToRemove] = useState<{ id: string; user_email: string } | null>(null);

  useEffect(() => {
    const fetchSharedUsers = async () => {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('agent_sharing')
        .select('id, user_email')
        .eq('agent_id', agentId);

      if (error) {
        console.error('Error fetching shared users:', error);
      } else if (data) {
        setSharedUsers(data);
      }
      setIsLoading(false);
    };

    fetchSharedUsers();
  }, [agentId]);

  const handleRemoveClick = (user: { id: string; user_email: string }) => {
    setUserToRemove({ id: user.id, user_email: user.user_email });
  };

  const handleConfirmRemove = async () => {
    if (!userToRemove) return;

    try {
      await removeAgentSharing(userToRemove.id);
      setSharedUsers(prev => prev.filter(user => user.id !== userToRemove.id));
    } catch (error) {
      console.error('Failed to remove user:', error);
      // You might want to show a toast notification here
    } finally {
      setUserToRemove(null);
    }
  };

  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading...</p>;
  }

  if (sharedUsers.length === 0) {
    return <p className="text-sm text-gray-500">Not shared with anyone yet.</p>;
  }

  return (
    <>
      <ul className="space-y-2">
        {sharedUsers.map((user) => (
          <li key={user.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-800">{user.user_email}</span>
            <button 
              onClick={() => handleRemoveClick(user)}
              className="text-gray-400 hover:text-red-600 p-1 rounded-full transition-colors"
              title="Remove user"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd"></path></svg>
            </button>
          </li>
        ))}
      </ul>
      <ConfirmationModal
        isOpen={!!userToRemove}
        onClose={() => setUserToRemove(null)}
        onConfirm={handleConfirmRemove}
        title="Remove User"
        message={`Are you sure you want to remove ${userToRemove?.user_email} from this agent?`}
      />
    </>
  );
};

const Step4DeployAndShare: React.FC<Step4DeployAndShareProps> = ({
  agentId,
  formData,
  updateFormData
}) => {
  const [showSandbox, setShowSandbox] = React.useState(false);
  const { whatsappConfig, telegramConfig, apiConfig, sharable } = formData;
  const inviteLink = agentId ? `${window.location.origin}/invite/${agentId}` : '';
  
  const [isSettingWebhook, setIsSettingWebhook] = useState(false);
  const [webhookSuccess, setWebhookSuccess] = useState<string | null>(null);
  const [webhookError, setWebhookError] = useState<string | null>(null);

  const onWhatsappConfigChange = (config: WhatsAppConfig) => {
    updateFormData('whatsappConfig', config);
  };

  const onTelegramConfigChange = (config: TelegramConfig) => {
    updateFormData('telegramConfig', config);
  };

  const onApiConfigChange = (config: ApiConfig) => {
    updateFormData('apiConfig', config);
  };
  
  const handleSharableToggle = (enabled: boolean) => {
    updateFormData('sharable', enabled);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // TODO: Show a toast notification
  };

  const handleSetTelegramBot = async () => {
    if (!agentId || !telegramConfig.botToken) {
      setWebhookError("Bot Token is required.");
      return;
    }

    setIsSettingWebhook(true);
    setWebhookSuccess(null);
    setWebhookError(null);

    try {
      // 1. Save the token to the database
      await updateAgent(agentId, { telegramConfig: telegramConfig });

      // 2. Call the webhook setup endpoint
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const response = await fetch(`https://xbg3utjjpp.eu-central-1.awsapprunner.com/v1/agents/${agentId}/telegram-bot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ bot_token: telegramConfig.botToken })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to set up Telegram bot webhook.');
      }

      setWebhookSuccess("Telegram bot successfully connected and webhook is set!");

    } catch (error: any) {
      console.error("Error setting up Telegram bot:", error);
      setWebhookError(error.message || "An unknown error occurred.");
    } finally {
      setIsSettingWebhook(false);
    }
  };

  const curlCommand = `curl -X POST api.openagents.columen.eu/v1/chat
  -H "X-API-Key: oa-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
  -F "agent_id=${agentId || 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'}"
  -F "message=Hi, can you help me with this problem?"
  -F "session_id=xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
  -F "stream=false"`;

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Deploy & Share Your Bot</h3>
        <p className="text-gray-600">Test your agent in our sandbox and connect it to platforms</p>
      </div>

      {/* Sharing Section */}
      <div className="bg-white rounded-xl p-6 space-y-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-8.316l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"></path></svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-900">Enable Sharing</h4>
          </div>
          <ToggleSwitch
            id="sharable-enabled"
            label=""
            checked={!!sharable}
            onChange={handleSharableToggle}
          />
        </div>

        {sharable && agentId && (
          <div className="space-y-6 pt-4 border-t border-gray-100">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Invite Link</label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  readOnly
                  value={inviteLink}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm text-gray-700"
                />
                <button 
                  onClick={() => copyToClipboard(inviteLink)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-800 transition-colors"
                >
                  Copy
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Shared With</label>
              <SharedWithList agentId={agentId} />
            </div>
          </div>
        )}
      </div>

      {/* Sandbox Section */}
      <div className="bg-white rounded-xl border border-gray-200">
        <button
          onClick={() => setShowSandbox(!showSandbox)}
          className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors rounded-xl"
        >
          <div>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">Test Your Agent</h3>
            </div>
            <p className="text-gray-600 mt-1">Try your agent directly through WhatsApp and Telegram</p>
          </div>
          <svg className={`w-5 h-5 text-gray-400 transition-transform ${showSandbox ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {showSandbox && (
          <div className="px-6 pb-6 border-t border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {/* WhatsApp Sandbox */}
              <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <WhatsAppIcon className="w-6 h-6 text-green-600" />
                  <h4 className="text-lg font-semibold text-green-800">WhatsApp Sandbox</h4>
                </div>
                
                <div className="text-center mb-4">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=128x128&data=${encodeURIComponent(`https://wa.me/+14155238886?text=join%20sandbox-${agentId || 'demo'}`)}`}
                    alt="WhatsApp QR Code"
                    className="w-32 h-32 mx-auto mb-3 rounded-lg border-2 border-gray-200"
                  />
                  <p className="text-sm text-gray-600 mb-2">Scan QR code or click link to start testing</p>
                  <a 
                    href={`https://wa.me/+14155238886?text=join%20sandbox-${agentId || 'demo'}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                  >
                    <WhatsAppIcon className="w-4 h-4" />
                    <span>Test on WhatsApp</span>
                  </a>
                </div>
                
                <div className="bg-green-100 border border-green-300 rounded-lg p-3 mb-4">
                  <p className="text-xs text-green-800 font-medium mb-2">
                    <strong>Step 1:</strong> Join the sandbox
                  </p>
                  <div className="bg-white p-2 rounded border text-xs font-mono text-gray-700">
                    join sandbox-{agentId || 'demo'}
                  </div>
                </div>
                
                <div className="bg-green-100 border border-green-300 rounded-lg p-3">
                  <p className="text-xs text-green-800 font-medium mb-2">
                    <strong>Step 2:</strong> Test your agent with example messages
                  </p>
                  <div className="space-y-2">
                    <div className="bg-white p-2 rounded border text-xs font-mono text-gray-700">
                      Hello, can you help me?
                    </div>
                    <div className="bg-white p-2 rounded border text-xs font-mono text-gray-700">
                      What can you do?
                    </div>
                    <div className="bg-white p-2 rounded border text-xs font-mono text-gray-700">
                      Tell me about your capabilities
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Telegram Sandbox */}
              <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <TelegramIcon className="w-6 h-6 text-blue-600" />
                  <h4 className="text-lg font-semibold text-blue-800">Telegram Sandbox</h4>
                </div>
                
                <div className="text-center mb-4">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=128x128&data=${encodeURIComponent(`https://t.me/noco_sandbox_bot?start=${agentId || 'demo'}`)}`}
                    alt="Telegram QR Code"
                    className="w-32 h-32 mx-auto mb-3 rounded-lg border-2 border-gray-200"
                  />
                  <p className="text-sm text-gray-600 mb-2">Scan QR code or click link to start testing</p>
                  <a 
                    href={`https://t.me/noco_sandbox_bot?start=${agentId || 'demo'}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    <TelegramIcon className="w-4 h-4" />
                    <span>Test on Telegram</span>
                  </a>
                </div>
                
                <div className="bg-blue-100 border border-blue-300 rounded-lg p-3 mb-4">
                  <p className="text-xs text-blue-800 font-medium mb-2">
                    <strong>Step 1:</strong> Start the bot
                  </p>
                  <div className="bg-white p-2 rounded border text-xs font-mono text-gray-700">
                    /start {agentId || 'demo'}
                  </div>
                </div>
                
                <div className="bg-blue-100 border border-blue-300 rounded-lg p-3">
                  <p className="text-xs text-blue-800 font-medium mb-2">
                    <strong>Step 2:</strong> Test your agent with example messages
                  </p>
                  <div className="space-y-2">
                    <div className="bg-white p-2 rounded border text-xs font-mono text-gray-700">
                      Hello, can you help me?
                    </div>
                    <div className="bg-white p-2 rounded border text-xs font-mono text-gray-700">
                      What can you do?
                    </div>
                    <div className="bg-white p-2 rounded border text-xs font-mono text-gray-700">
                      Tell me about your capabilities
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <ErrorIcon className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <h6 className="font-medium text-yellow-800 mb-1">Important Notes</h6>
                  <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                    <li>The sandbox uses your current agent configuration</li>
                    <li>Changes to instructions or settings are reflected immediately</li>
                    <li>Both sandbox bots are shared environments for testing purposes only</li>
                    <li>For production use, deploy your agent using the configuration below</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* API Section */}
      <div className="bg-white rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"></path></svg>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900">API Access</h4>
            </div>
          </div>
          <ToggleSwitch
            id="api-enabled"
            label=""
            checked={apiConfig.enabled}
            onChange={(checked) => onApiConfigChange({
              ...apiConfig,
              enabled: checked
            })}
          />
        </div>

        {apiConfig.enabled && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">API Endpoint</label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  readOnly
                  value="api.openagents.columen.eu/v1/chat"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm text-gray-700"
                />
                <button 
                  onClick={() => copyToClipboard("api.openagents.columen.eu/v1/chat")}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-800 transition-colors"
                >
                  Copy
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Example Request (cURL)</label>
              <div className="relative bg-gray-800 rounded-lg p-4 text-sm text-white font-mono">
                <button 
                  onClick={() => copyToClipboard(curlCommand)}
                  className="absolute top-2 right-2 px-2 py-1 bg-gray-600 hover:bg-gray-700 rounded-lg text-xs font-medium text-gray-200 transition-colors"
                >
                  Copy
                </button>
                <pre><code>{curlCommand}</code></pre>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h5 className="font-medium text-blue-900 mb-2">📋 API Documentation</h5>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>X-API-Key:</strong> Your API key for authentication. You can get it from your <Link to="/profile" className="underline">profile page</Link>.</li>
                <li>• <strong>agent_id:</strong> Your unique agent identifier</li>
                <li>• <strong>message:</strong> The user's message to send to the agent</li>
                <li>• <strong>session_id:</strong> uuid Unique session identifier (leave empty for new sessions)</li>
                <li>• <strong>stream:</strong> Whether to stream the response (true/false)</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* WhatsApp Section */}
      <div className="bg-white rounded-xl p-6 space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.382"/>
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-900">WhatsApp</h4>
          </div>
          <ToggleSwitch
            id="whatsapp-enabled"
            label=""
            checked={whatsappConfig.enabled}
            onChange={(checked) => onWhatsappConfigChange({
              ...whatsappConfig,
              enabled: checked
            })}
          />
        </div>

        {whatsappConfig.enabled && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h5 className="font-medium text-blue-900 mb-2">📋 Setup Instructions</h5>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Create a <a href="https://www.twilio.com" target="_blank" rel="noopener noreferrer" className="underline">Twilio account</a></li>
                <li>Get your Account SID and Auth Token from Twilio Console</li>
                <li>Purchase a phone number or use Twilio Sandbox</li>
                <li>Enable WhatsApp messaging for your number</li>
              </ol>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Twilio Account SID *</label>
                <input
                  type="text"
                  value={whatsappConfig.twilioAccountSid || ''}
                  onChange={(e) => onWhatsappConfigChange({
                    ...whatsappConfig,
                    twilioAccountSid: e.target.value
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal focus:border-transparent text-sm"
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Auth Token *</label>
                <input
                  type="password"
                  value={whatsappConfig.twilioAuthToken || ''}
                  onChange={(e) => onWhatsappConfigChange({
                    ...whatsappConfig,
                    twilioAuthToken: e.target.value
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal focus:border-transparent text-sm"
                  placeholder="Your Twilio Auth Token"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Number *</label>
              <input
                type="tel"
                value={whatsappConfig.phoneNumber || ''}
                onChange={(e) => onWhatsappConfigChange({
                  ...whatsappConfig,
                  phoneNumber: e.target.value
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal focus:border-transparent text-sm"
                placeholder="+1234567890"
              />
            </div>

            <WhitelistManager
              title="Authorized Phone Numbers"
              items={whatsappConfig.whitelistedNumbers}
              placeholder="+1234567890"
              validator={validatePhoneNumber}
              validationMessage="Please enter a valid phone number with country code (e.g., +1234567890)"
              onItemsChange={(items) => onWhatsappConfigChange({
                ...whatsappConfig,
                whitelistedNumbers: items
              })}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Unauthorized User Message</label>
              <textarea
                value={whatsappConfig.unauthorizedMessage || ''}
                onChange={(e) => onWhatsappConfigChange({
                  ...whatsappConfig,
                  unauthorizedMessage: e.target.value
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal focus:border-transparent text-sm"
                rows={2}
                placeholder="Message shown to unauthorized users"
              />
            </div>
          </div>
        )}
      </div>

      {/* Telegram Section */}
      <div className="bg-white rounded-xl p-6 space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-900">Telegram</h4>
          </div>
          <ToggleSwitch
            id="telegram-enabled"
            label=""
            checked={telegramConfig.enabled}
            onChange={(checked) => onTelegramConfigChange({
              ...telegramConfig,
              enabled: checked
            })}
          />
        </div>

        {telegramConfig.enabled && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h5 className="font-medium text-blue-900 mb-2">🤖 Setup Instructions</h5>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Message <a href="https://t.me/botfather" target="_blank" rel="noopener noreferrer" className="underline">@BotFather</a> on Telegram</li>
                <li>Send /newbot and follow the instructions</li>
                <li>Copy the Bot Token (looks like: 123456789:ABCdefGhIJKlmNoPQRsTUVwxyZ)</li>
                <li>Set your bot's description and commands</li>
              </ol>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bot Token *</label>
              <div className="flex items-start space-x-2">
                <input
                  type="password"
                  value={telegramConfig.botToken || ''}
                  onChange={(e) => {
                    onTelegramConfigChange({ ...telegramConfig, botToken: e.target.value });
                    setWebhookError(null);
                    setWebhookSuccess(null);
                  }}
                  className="flex-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal focus:border-transparent text-sm"
                  placeholder="123456789:ABCdefGhIJKlmNoPQRsTUVwxyZ"
                />
                <button
                  onClick={handleSetTelegramBot}
                  disabled={isSettingWebhook}
                  className="px-4 py-2 bg-netlify-teal text-white rounded-lg hover:bg-netlify-600 transition-colors text-sm font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isSettingWebhook ? 'Setting...' : 'Set Bot'}
                </button>
              </div>
              {webhookError && <p className="text-sm text-red-600 mt-2">{webhookError}</p>}
              {webhookSuccess && <p className="text-sm text-green-600 mt-2">{webhookSuccess}</p>}
            </div>

            <WhitelistManager
              title="Authorized Users"
              items={telegramConfig.whitelistedUsers}
              placeholder="@username or 123456789"
              validator={validateTelegramUser}
              validationMessage="Please enter a valid Telegram username (@username) or user ID (numbers only)"
              onItemsChange={(items) => onTelegramConfigChange({
                ...telegramConfig,
                whitelistedUsers: items
              })}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Unauthorized User Message</label>
              <textarea
                value={telegramConfig.unauthorizedMessage || ''}
                onChange={(e) => onTelegramConfigChange({
                  ...telegramConfig,
                  unauthorizedMessage: e.target.value
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal focus:border-transparent text-sm"
                rows={2}
                placeholder="Message shown to unauthorized users"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};



export default Step4DeployAndShare;

